import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { User } from 'oidc-client';
import { Subscription } from 'rxjs';
import { IClientPortalUser } from 'src/app/models/Client';
import { UserProfile } from 'src/app/models/UserProfile';
import { AuthService } from 'src/app/services/auth.service';
import { UsersService } from 'src/app/services/users.service';

interface SidebarMenu {
    title: string;
    collapsedTitle?: string;
    icon?: string;
    show: boolean;
    url?: string;
    selected: boolean;
    disabled?: boolean;
    children?: SidebarMenu[];
}

@Component({
    selector: 'app-sidebar',
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.scss'],
    animations: [
        trigger( 'collapseToggle', [
            state('collapse', style({
                width: '150px'
            }), ),
            state('full', style({
                width: '300px'
            })),
            transition('full => collapse', [
                animate('0.2s')
            ]),
            transition('collapse => full', [
                animate('0.2s ease-out')
            ]),
        ] ),
        trigger( 'sidebarTextCollapse', [
            state('collapseText', style({
                display: 'none'
            })),
            state('fullText', style({
                display: 'inline'
            })),
            transition('collapseText => fullText', [
                animate('0s 0.5s')
            ]),
        ]),
        trigger('submenuAnimation', [
            state('closed', style({
                maxHeight: '0px',
                opacity: 0,
                transform: 'translateY(-8px)',
                overflow: 'hidden'
            })),
            state('open', style({
                maxHeight: '300px',
                opacity: 1,
                transform: 'translateY(0)',
                overflow: 'hidden'
            })),
            transition('closed <=> open', [
                animate('220ms ease-in-out')
            ])
        ]),
        trigger('childItemAnimation', [
            state('hidden', style({
                opacity: 0,
                transform: 'translateX(-8px)'
            })),
            state('visible', style({
                opacity: 1,
                transform: 'translateX(0)'
            })),
            transition('hidden <=> visible', [
                animate('180ms ease-in-out')
            ])
        ])
    ]
})
export class SidebarComponent implements OnInit, OnDestroy {

    menus: SidebarMenu[] = [
        {
            title: 'HOME',
            icon: 'house',
            show: false,
            url: '/',
            selected: true
        },
        {
            title: 'CONTRACTS',
            icon: 'folder-open',
            selected: false,
            show: false,
            children: [
                {
                    title: 'MANAGE_CONTRACTS',
                    show: false,
                    url: 'contracts',
                    selected: false
                },
                {
                    title: 'ACCESS_REQUESTS',
                    show: false,
                    url: 'contracts/access-requests',
                    selected: false
                }
            ]
        },
        {
            title: 'PROPOSALS',
            icon: 'folder-open',
            selected: false,
            show: false,
            children: [
                {
                    title: 'MANAGE_PROPOSALS',
                    show: false,
                    url: 'proposals',
                    selected: false
                }
            ]
        },
        {
            title: 'MARKET_DEALERS',
            collapsedTitle: 'M.D',
            icon: 'chart-mixed',
            selected: false,
            show: false,
            url: 'market-dealers/dashboard',
            children: [
                {
                    title: 'Hedging',
                    show: false,
                    url: 'market-dealers/hedging',
                    selected: false
                },
                {
                    title: 'Spot',
                    show: false,
                    url: 'market-dealers/spot',
                    selected: false,
                    disabled: true
                }
            ]
        },
        {
            title: 'SUPPORT_REQUESTS',
            collapsedTitle: 'SUPPORT',
            icon: 'folder-open',
            selected: false,
            show: false,
            children: [
                {
                    title: 'MESSAGES',
                    show: false,
                    url: 'tickets/messages',
                    selected: false
                },
                {
                    title: 'NEW_REQUEST',
                    show: false,
                    url: 'tickets/new',
                    selected: false
                }
            ]
        }
    ]

    menuIsCollapsed: boolean = false

    avatarSafeUrl?: SafeHtml

    subscriptions: Subscription[] = []

    user?: IClientPortalUser = undefined;

    constructor( private _userService: UsersService, private _sanitizer: DomSanitizer, private _router: Router ) {
        let userDataSubscription: Subscription = _userService.userData$.subscribe(
            (user) => {
                if (user != null) {
                    this.user = user;

                    if (user.avatar) {
                        this.avatarSafeUrl = _sanitizer.bypassSecurityTrustUrl('data:image/jpeg;base64,' + user.avatar);
                    }

                }
            }
        );

        this.subscriptions.push(userDataSubscription);
    }

    ngOnInit(): void {
    }

    triggerMenuFoldOrUnfold() {
        this.menuIsCollapsed = !this.menuIsCollapsed;
    }

    openSubMenu(menu: any, canNavigate: boolean) {
        if (menu.url && canNavigate) {
            this.navigateTo(menu.url)
            return;
        }

        if (!menu.children || menu.children.length === 0) {
            return;
        }

        this.menus.forEach((item: any) => {
            if (item !== menu && item.children) {
                item.show = false;
            }
        });

        menu.show = !menu.show;
    }

    navigateTo(url?: string) {
        if (url) {
            this._router.navigate([url]);
        }
        
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((subscription) => {
            subscription.unsubscribe();
        })
    }
}
